import { useCallback, useMemo } from 'react';
import {
	Flex,
	FlexItem,
	Message,
	Panel,
	ProgressCircle,
	TableFigure,
} from '@bigcommerce/big-design';
import {
	useCurrency,
	useCustomerGroup,
	useCustomerGroupAndProductTypes,
	useOrderProducts,
} from '../../../context';
import { useSearchResults } from '../../../hooks/useSearchResults';
import { addCustomProducts } from '../../../store/orderProducts';
import useProductsSearchStore from '../../../store/productsSearch';
import SearchResults from '../../SearchResults';
import { VehicleSelection } from '../../VehicleSelection';
import CustomProduct from './CustomProduct';
import { ProductFiltersSelection } from './ProductFiltersSelection';

export function ProductsSearchSection() {
	const [currency] = useCurrency();
	const [customerGroup] = useCustomerGroup();
	const [, dispatchOrderProducts] = useOrderProducts();

	const [productSearchState, dispatchProductSearch] =
		useProductsSearchStore();

	const [isLoadingSearchResults, searchResults] = useSearchResults(
		productSearchState.filters.vehicle.year.selected,
		productSearchState.filters.vehicle.make.selected,
		productSearchState.filters.vehicle.model.selected,
		productSearchState.filters.vehicle.subModel.selected,
		productSearchState.filters.product.productType.selected,
		productSearchState.filters.product.productName.selected,
		productSearchState.filters.product.color.selected,
		customerGroup,
	);
	const [isLoadingFilters] = useCustomerGroupAndProductTypes();

	const allOptionsSelected =
		productSearchState.filters.vehicle.year.selected &&
		productSearchState.filters.vehicle.make.selected &&
		productSearchState.filters.vehicle.model.selected &&
		productSearchState.filters.product.productType.selected &&
		productSearchState.filters.product.productName.selected &&
		productSearchState.filters.product.color.selected;

	const searchMessage = useMemo(() => {
		// Inform the user they need to fill out all options to see results.
		if (!allOptionsSelected) {
			return [
				{ text: 'Fill out all filter options to see search results.' },
			];
		}

		// User has filled out all required fields, but no results were found.
		if (searchResults.length === 0) {
			return [
				{
					text: 'No products found with your filters. Please refine and try again.',
				},
			];
		}

		// There are results and we've selected all filters, no message to display.
		return [];
	}, [allOptionsSelected, searchResults]);

	const addCustomProductToOrder = useCallback(
		(
			description,
			color,
			partNumber,
			year,
			make,
			model,
			seatDescription,
			row,
			vendorName,
			priceIncTax,
			priceExcTax,
		) =>
			dispatchOrderProducts(
				addCustomProducts({
					product: description,
					color,
					partNumber,
					year,
					make,
					model,
					seatDescription,
					row,
					vendorName,
					priceExcTax,
					priceIncTax,
				}),
			),
		[productSearchState.filters, dispatchOrderProducts],
	);

	return (
		<Panel header="Add Products" marginTop="xxxLarge">
			<Flex
				className="filters"
				justifyContent="space-between"
				marginTop="xLarge"
			>
				<FlexItem flexGrow={1}>
					<div className="product-item-subheading">
						Vehicle Information
					</div>
					<VehicleSelection
						vehiclesState={productSearchState.filters.vehicle}
						updateSelection={dispatchProductSearch}
					/>
				</FlexItem>
				<FlexItem flexGrow={1}>
					<div className="product-item-subheading">
						Product Options
					</div>
					<ProductFiltersSelection
						filters={productSearchState.filters}
						filtersDispatch={dispatchProductSearch}
					/>
				</FlexItem>
			</Flex>

			<div className="product-item-subheading">
				Product Search Results
			</div>
			<TableFigure>
				<div className="custom-product">
					<CustomProduct
						onAddCustomProduct={addCustomProductToOrder}
					/>
				</div>
				<SearchResults
					products={allOptionsSelected ? searchResults : []}
					currency={currency}
				/>
				{(!allOptionsSelected || searchResults.length === 0) && (
					<Message
						type="info"
						marginHorizontal="xxSmall"
						marginVertical="small"
						messages={searchMessage}
					/>
				)}
			</TableFigure>
			{(isLoadingSearchResults ||
				isLoadingFilters ||
				productSearchState.search.isLoading) && (
				<div className="loading-cover">
					<ProgressCircle
						className="loading-indicator"
						size="large"
					/>
				</div>
			)}
		</Panel>
	);
}
